import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import { CheckIcon, FlameIcon } from '@shared/ui';
import type { StreakReward } from '@shared/types';
import { calendarWeek, mskWeekday } from '../lib/week';

interface StreakStripProps {
  streak: number;
  /** 7 дней МСК, от шести дней назад до сегодня. */
  weekDays: boolean[];
  dayCounted: boolean;
  /** Когда серия даст несгораемые жетоны; null — начисления выключены или старый бэкенд. */
  reward: StreakReward | null;
  phone: boolean;
}

/**
 * Строка серии внизу карточки «Тренировка на сегодня» (новая главная, решение владельца 2026-10-22): «N дней
 * подряд», неделя пн–вс и справа — когда серия даст жетоны («+2 жетона через 2 дня») вместо прежнего «до недели»:
 * понятный повод прийти завтра. Тренировался — кружок залит, сегодня ещё нет — обведён, пропуски и будущие дни —
 * бледные.
 */
export function StreakStrip({ streak, weekDays, dayCounted, reward, phone }: StreakStripProps) {
  const { t } = useTranslation();
  const today = mskWeekday();
  const week = calendarWeek(weekDays, today);
  const labels = t('today.weekdays').split(',');
  const trained = week.filter(Boolean).length;
  const streakTitle = streak > 0 ? t('today.streak', { count: streak }) : t('today.streakStart');
  const tokens = reward ? t('today.rewardTokens', { count: reward.tokens }) : '';
  const when = !reward
    ? ''
    : reward.inDays === 0
      ? t('today.rewardToday')
      : reward.inDays === 1
        ? t('today.rewardTomorrow')
        : t('today.rewardIn', { count: reward.inDays });

  const flame = (size: 'sm' | 'md') => (
    <span
      aria-hidden
      className={cn('grid flex-none place-items-center rounded-full bg-accent/15 text-accent', size === 'sm' ? 'h-6 w-6' : 'h-7 w-7')}
    >
      <FlameIcon className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
    </span>
  );

  if (phone) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
        <span className="flex min-w-0 items-center gap-2 text-[15px] font-semibold leading-5 text-fg">
          {flame('sm')}
          {streakTitle}
        </span>
        <span role="img" aria-label={t('today.week', { count: trained })} className="flex flex-none items-center gap-[3px]">
          {week.map((on, i) => (
            <span
              key={i}
              aria-hidden
              className={cn(
                'flex h-[22px] w-[22px] items-center justify-center rounded-full text-[9px] font-semibold leading-none',
                on
                  ? 'bg-accent text-fg-inv'
                  : i === today
                    ? 'text-fg ring-1 ring-inset ring-accent'
                    : on === null
                      ? 'text-white/30'
                      : 'bg-white/[0.07] text-muted',
              )}
            >
              {labels[i] ?? ''}
            </span>
          ))}
        </span>
        {reward && (
          <span title={t('today.rewardHint')} className="w-full text-[13px] font-medium leading-[18px] text-accent">
            {tokens} {when}
          </span>
        )}
      </div>
    );
  }

  // Уже ≈ 480 px строка не умещается — награда переносится под неделю, а не обрезает «N дней подряд».
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
      <span className="flex min-w-[150px] flex-1 items-center gap-2.5">
        {flame('md')}
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-semibold leading-5 text-fg">{streakTitle}</span>
          <span className="block truncate text-[13px] leading-[18px]">
            {dayCounted ? (
              <span className="inline-flex items-center gap-1 text-success">
                <CheckIcon className="h-3 w-3 flex-none" aria-hidden />
                {t('today.dayCounted')}
              </span>
            ) : (
              <span className="text-muted">{t('today.dayPendingShort')}</span>
            )}
          </span>
        </span>
      </span>
      <span role="img" aria-label={t('today.week', { count: trained })} className="grid flex-none grid-cols-7 gap-x-1.5">
        {week.map((on, i) => (
          <span
            key={i}
            aria-hidden
            className={cn('flex flex-col items-center gap-1 text-[11px] leading-none', i === today ? 'font-semibold text-fg' : 'text-muted')}
          >
            {labels[i] ?? ''}
            <span
              className={cn(
                'h-3 w-3 rounded-full',
                on ? 'bg-accent' : i === today ? 'border-2 border-solid border-accent' : on === null ? 'bg-white/[0.05]' : 'bg-white/[0.12]',
              )}
            />
          </span>
        ))}
      </span>
      {reward && (
        <span title={t('today.rewardHint')} className="ml-auto flex-none text-right">
          <span className="block text-[13px] font-semibold leading-[18px] text-accent">{tokens}</span>
          <span className="block text-[12px] leading-4 text-muted">{when}</span>
        </span>
      )}
    </div>
  );
}
